/**
 * spaContextStore — "what's on screen right now" snapshot (2026-07-01;
 * moduleId-keyed multi-publisher 1.424.0 tiling P3).
 *
 * A shared, structured description of what the user is currently looking at.
 * Views/modules PUBLISH into this store (setSpaContext) when their view or
 * data changes and CLEAR their own entry on unmount/close.
 *
 * WHY this exists: the voice agent ("BRIDGE") and the Pi chat agent had NO
 * visibility into the active module's data, so when asked "read me the
 * contacts" they hallucinated placeholder names. This store is the bridge that
 * lets the agents actually SEE the screen:
 *   - voiceStore sends `getSpaContext()` as `spa_context` on every voice turn →
 *     the backend injects it into the dispatcher's system prompt.
 *   - spaContextInjector appends it to the Pi agent's systemPrompt each turn.
 *
 * P3 (concurrent module tiling): the store holds ONE entry PER PUBLISHER,
 * keyed by moduleId (modules) or `view:{view}` (core views). Pre-tiling the
 * single slot meant concurrent tiles clobbered each other and one tile's
 * unmount cleared a sibling's snapshot. `getSpaContext()` assembles the wire
 * payload READ-TIME from uiStore state:
 *   - non-modules view → that view's own snapshot (legacy single shape);
 *   - modules view, one open snapshot → legacy single shape (back-compat);
 *   - modules view, 2+ open snapshots → `{modules: {id: snap}, focusedId}`.
 * Modules-view payloads also carry `available` (permitted modules) so agents
 * know what `open_module` can open.
 *
 * Kept deliberately GENERAL (view / title / detail / items) so ANY module or
 * core view can publish without this store knowing module specifics. `items`
 * are pre-formatted human-readable lines produced by the publisher (it knows
 * its own data best); the store only caps volume so the snapshot stays
 * prompt-sized.
 */
import { create } from 'zustand';
import { useUIStore } from './uiStore';
import { useModuleStore } from './moduleStore';
import { actionsForKey, type ModuleActionSpec } from '@/components/modules/moduleActions';

/** Max data rows carried in a snapshot (keeps the injected prompt bounded). */
export const SPA_CONTEXT_MAX_ITEMS = 50;
/** Max characters per row line (defensive against long notes / messages). */
export const SPA_CONTEXT_MAX_ITEM_CHARS = 200;

export interface SpaContext {
  /** Top-level view, e.g. 'modules' | 'designer' | 'knowledge' | 'terminals'. */
  view: string;
  /** Short title of what's shown, e.g. 'GoHighLevel — Contacts'. */
  title: string;
  /** One-line summary incl. counts, e.g. '142 contacts (showing 50)'. */
  detail: string;
  /** Capped, human-readable data lines — the actual on-screen rows. */
  items: string[];
  /** Owning module id when a module is active (e.g. 'gamemagick.ghl'). */
  moduleId?: string;
  /** Active sub-view id within the module/view (e.g. 'contacts'). */
  activeView?: string;
  /** The `vibe:module-action` verbs an agent may dispatch to this module.
   * Attached read-time in getSpaContext() from the moduleActions catalog (the
   * single source of truth) — publishers never set this. Absent when the
   * on-screen thing has no module-scoped verbs (core views, verb-less modules). */
  actions?: ModuleActionSpec[];
}

/** Module the tenant may open (subset of moduleStore's permitted list) —
 * carried on modules-view payloads so agents can target `open_module`. */
export interface AvailableModule {
  id: string;
  name: string;
}

/** Wire shape when 2+ module tiles are on screen: every open tile's snapshot
 * keyed by moduleId + which one has focus. Single-snapshot payloads keep the
 * plain SpaContext shape so pre-P3 backends parse them unchanged. */
export interface MultiSpaContext {
  modules: Record<string, SpaContext>;
  focusedId: string | null;
  available?: AvailableModule[];
}

export type SpaContextPayload = (SpaContext & { available?: AvailableModule[] }) | MultiSpaContext;

interface SpaContextState {
  /** One snapshot per publisher — moduleId key for modules, `view:{view}` for
   * core views. Insertion order is the open/publish order. */
  contexts: Record<string, SpaContext>;
  setSpaContext: (ctx: SpaContext) => void;
  /** Remove one publisher's entry (pass your own key); no-arg clears ALL. */
  clearSpaContext: (key?: string) => void;
}

/** Store key for a snapshot: its moduleId, else its view namespaced. */
export function contextKey(ctx: SpaContext): string {
  return ctx.moduleId ?? `view:${ctx.view}`;
}

/** Cap the row count and truncate over-long lines so an injected snapshot
 * stays within a sane prompt budget regardless of what a publisher passes. */
export function capSpaContextItems(items: string[]): string[] {
  return items.slice(0, SPA_CONTEXT_MAX_ITEMS).map((s) => {
    const line = typeof s === 'string' ? s : String(s);
    return line.length > SPA_CONTEXT_MAX_ITEM_CHARS
      ? `${line.slice(0, SPA_CONTEXT_MAX_ITEM_CHARS - 1)}…`
      : line;
  });
}

export const useSpaContextStore = create<SpaContextState>((set) => ({
  contexts: {},
  setSpaContext: (ctx) =>
    set((s) => ({
      contexts: {
        ...s.contexts,
        [contextKey(ctx)]: { ...ctx, items: capSpaContextItems(ctx.items) },
      },
    })),
  clearSpaContext: (key) =>
    set((s) => {
      if (key === undefined) return { contexts: {} };
      if (!(key in s.contexts)) return s;
      const next = { ...s.contexts };
      delete next[key];
      return { contexts: next };
    }),
}));

/** Permitted modules as the compact `available` list (empty until loaded). */
function availableModules(): AvailableModule[] {
  return useModuleStore
    .getState()
    .permittedModules.map((m) => ({ id: m.module_id, name: m.display_name || m.module_id }));
}

/** Attach the module's declared verbs (single source: moduleActions catalog) to
 * a snapshot. UNCONDITIONAL — NOT gated on `available` (which is empty until
 * moduleStore loads); the actions come from the static catalog, not that store.
 * No-op when the key has no module-scoped verbs. */
function withActions(snap: SpaContext): SpaContext {
  const actions = actionsForKey(contextKey(snap));
  return actions.length ? { ...snap, actions } : snap;
}

/** Read the current snapshot payload for a dispatch turn (voice/Pi).
 * Assembled read-time from uiStore so stale entries from hidden views or
 * closed tiles never leak into the prompt. Returns null when nothing relevant
 * is published. */
export function getSpaContext(): SpaContextPayload | null {
  const { contexts } = useSpaContextStore.getState();
  const { viewMode, openModuleIds, activeModuleId } = useUIStore.getState();

  if (viewMode !== 'modules') {
    const c = contexts[`view:${viewMode}`];
    return c ? withActions(c) : null;
  }

  let entries = Object.entries(contexts).filter(([key]) => !key.startsWith('view:'));
  if (openModuleIds.length > 0) {
    const open = new Set(openModuleIds);
    entries = entries.filter(([key]) => open.has(key));
  }
  if (entries.length === 0) {
    // Back-compat: a modules-view publisher that omitted moduleId lands under
    // 'view:modules' — surface it rather than dropping the snapshot.
    const legacy = contexts['view:modules'];
    return legacy ? withActions(legacy) : null;
  }

  const available = availableModules();
  if (entries.length === 1) {
    // Back-compat: single-tile payload keeps the legacy flat shape. `actions`
    // is attached unconditionally; only `available` stays gated on the store.
    const single = withActions(entries[0][1]);
    return available.length ? { ...single, available } : single;
  }

  const modules = Object.fromEntries(entries.map(([k, v]) => [k, withActions(v)]));
  const focusedId =
    activeModuleId && modules[activeModuleId] ? activeModuleId : entries[entries.length - 1][0];
  return available.length ? { modules, focusedId, available } : { modules, focusedId };
}

/** Type guard for the multi-tile wire shape. */
export function isMultiSpaContext(p: SpaContextPayload): p is MultiSpaContext {
  return typeof p === 'object' && p !== null && 'modules' in p && !('title' in p);
}
